import React, { useState } from 'react';
import { Auth } from 'firebase/auth';
import { createPortal } from 'react-dom';
import './CamperAuthLinker.css';

interface CamperAuthLinkerProps {
  selectedCamper: {
    id: string;
    name: string;
    email: string;
    authUid?: string;
    loginExists?: boolean;
  } | null;
  onClose: () => void;
  auth: Auth;
  onLinkAccount: (camperId: string, authUid: string) => Promise<void>;
}

const CamperAuthLinker: React.FC<CamperAuthLinkerProps> = ({
  selectedCamper,
  onClose,
  onLinkAccount,
}) => {
  const [authUid, setAuthUid] = useState(selectedCamper?.authUid || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleModalClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!selectedCamper || !authUid.trim()) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await onLinkAccount(selectedCamper.id, authUid.trim());
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link account');
    } finally {
      setLoading(false);
    }
  };

  if (!selectedCamper) return null;

  const modalContent = (
    <div className="modal-backdrop" onClick={handleModalClick}>
      <div className="modal-content-wrapper">
        <button className="close-button" onClick={onClose}>&times;</button>
        <h3>Link Authentication Account</h3>

        <div className="camper-info">
          <div className="info-row">
            <strong>Name:</strong>
            <span>{selectedCamper.name}</span>
          </div>
          <div className="info-row">
            <strong>Email:</strong>
            <span>{selectedCamper.email}</span>
          </div>
          <div className="info-row">
            <strong>Current Auth UID:</strong>
            <span>{selectedCamper.authUid || 'None'}</span>
          </div>
          <div className="info-row">
            <strong>Has Login:</strong>
            <span className={`login-status ${selectedCamper.loginExists ? 'active' : 'inactive'}`}>
              {selectedCamper.loginExists ? 'Yes' : 'No'}
            </span>
          </div>
        </div>

        <div className="auth-input-section">
          <label htmlFor="authUid">Firebase Auth UID</label>
          <input
            id="authUid"
            type="text"
            value={authUid}
            onChange={(e) => setAuthUid(e.target.value)}
            placeholder="Enter Firebase Auth UID"
            disabled={loading}
          />
        </div>

        <button
          className="link-button"
          onClick={handleSubmit}
          disabled={loading || !authUid.trim()}
        >
          {loading ? 'Linking...' : 'Link Account'}
        </button>

        {error && <div className="error-message">{error}</div>}
        {success && (
          <div className="success-message">
            Successfully linked authentication account!
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CamperAuthLinker; 